import { apiUrl } from '../shared/config';

export type AuditFormSurface = 'solutions' | 'auth_signup';

export type LeadErrorReason =
  | 'timeout'
  | 'network'
  | 'validation'
  | 'rate_limit'
  | 'server'
  | 'unknown';

export type LeadSubmitResult =
  | { ok: true }
  | { ok: false; status: number | null; code: string | null; reason: LeadErrorReason };

export type LeadPayload = {
  email: string;
  name: string;
  role?: string;
  company?: string;
  website?: string;
  source?: string;
};

/** Client abort for POST /api/leads — the 2026-09-08 hang was silent without this. */
export const LEAD_SUBMIT_TIMEOUT_MS = 30_000;

function reasonFromStatus(status: number): LeadErrorReason {
  if (status === 429) return 'rate_limit';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 500) return 'server';
  return 'unknown';
}

async function readErrorCode(res: Response): Promise<string | null> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === 'object') {
      const rec = body as Record<string, unknown>;
      if (typeof rec.error === 'string') return rec.error;
      if (typeof rec.code === 'string') return rec.code;
    }
  } catch {
    return null;
  }
  return null;
}

function isAbortError(err: unknown): boolean {
  return Boolean(
    err && typeof err === 'object' && (err as { name?: string }).name === 'AbortError'
  );
}

/**
 * Public marketing lead capture. Uses `fetch`, not `apiFetch` (no auth header).
 */
export async function submitLead(payload: LeadPayload): Promise<LeadSubmitResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), LEAD_SUBMIT_TIMEOUT_MS);
  try {
    const res = await fetch(apiUrl('/api/leads'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: payload.email,
        name: payload.name,
        role: payload.role ?? '',
        company: payload.company ?? '',
        website: payload.website ?? '',
        source: payload.source ?? 'solutions-audit',
      }),
      signal: controller.signal,
    });
    if (!res.ok) {
      const code = await readErrorCode(res);
      return {
        ok: false,
        status: res.status,
        code,
        reason: reasonFromStatus(res.status),
      };
    }
    return { ok: true };
  } catch (err) {
    if (isAbortError(err)) {
      return { ok: false, status: null, code: null, reason: 'timeout' };
    }
    return { ok: false, status: null, code: null, reason: 'network' };
  } finally {
    clearTimeout(timer);
  }
}
